import { Link } from 'react-router-dom';
import { Briefcase } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-blue-900" />
              <span className="text-xl font-bold text-blue-900">JobSuiza</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Política de Privacidad</h1>
        <p className="text-sm text-slate-600 mb-8">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Información General</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Esta Política de Privacidad describe cómo JobSuiza ("nosotros", "nuestro" o "el Servicio") recopila, 
              usa y protege su información personal cuando utiliza nuestro sitio web jobsuiza.com.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Nos comprometemos a proteger su privacidad y cumplir con la Ley Federal Suiza de Protección de Datos (DSG) 
              y el Reglamento General de Protección de Datos de la UE (GDPR/RGPD).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Responsable del Tratamiento de Datos</h2>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-700 mb-2"><strong>Nombre:</strong> Lidia Moyano Acuna</p>
              <p className="text-slate-700 mb-2"><strong>Dirección:</strong> Tägernstrasse 1, 8127 Forch, Suiza</p>
              <p className="text-slate-700 mb-2"><strong>Email:</strong> info@jobsuiza.com</p>
              <p className="text-slate-700">
                <strong>Contacto de Privacidad:</strong> Para cualquier pregunta sobre privacidad, contáctenos en: info@jobsuiza.com
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Datos que Recopilamos</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">3.1 Datos Proporcionados por Usted</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Registro de cuenta:</strong> Nombre, dirección de correo electrónico, contraseña (encriptada)</li>
              <li><strong>Autenticación con Google:</strong> Nombre, email, foto de perfil (si usa Google Login)</li>
              <li><strong>Suscripción al Newsletter:</strong> Dirección de correo electrónico</li>
              <li><strong>Preferencias de búsqueda:</strong> Ubicación, sector, nivel de español, alertas de empleo</li>
              <li><strong>Trabajos guardados:</strong> Ofertas que marca como favoritas</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">3.2 Datos Recopilados Automáticamente</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Cookies y tecnologías similares:</strong> Información de sesión, preferencias, autenticación</li>
              <li><strong>Datos de uso:</strong> Páginas visitadas, tiempo en el sitio, clics en ofertas de trabajo</li>
              <li><strong>Información técnica:</strong> Dirección IP, tipo de navegador, dispositivo, sistema operativo</li>
              <li><strong>Google Analytics:</strong> Métricas agregadas de uso del sitio (anónimas cuando sea posible)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Cómo Usamos Sus Datos</h2>
            <p className="text-slate-700 leading-relaxed mb-4">Utilizamos sus datos personales para:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Proporcionar y mantener nuestro servicio de búsqueda de empleo</li>
              <li>Crear y gestionar su cuenta de usuario</li>
              <li>Personalizar su experiencia (filtros, alertas, trabajos guardados)</li>
              <li>Enviar alertas de empleo basadas en sus preferencias (si las activa)</li>
              <li><strong>Enviar nuestro newsletter</strong> con ofertas de trabajo destacadas y novedades (si se suscribe)</li>
              <li>Mejorar nuestro sitio web y servicios mediante análisis de uso</li>
              <li>Detectar y prevenir fraude o uso no autorizado</li>
              <li>Cumplir con obligaciones legales</li>
              <li>Comunicarnos con usted sobre su cuenta o el servicio</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Base Legal para el Tratamiento</h2>
            <p className="text-slate-700 leading-relaxed mb-4">Procesamos sus datos bajo las siguientes bases legales:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Consentimiento:</strong> Para cookies no esenciales, Google Analytics, marketing</li>
              <li><strong>Ejecución de contrato:</strong> Para proporcionar el servicio que solicitó</li>
              <li><strong>Interés legítimo:</strong> Para mejorar el servicio, seguridad, prevención de fraude</li>
              <li><strong>Cumplimiento legal:</strong> Para cumplir con obligaciones legales</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Compartir Datos con Terceros</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              No vendemos sus datos personales. Compartimos datos solo en las siguientes situaciones:
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">Proveedores de Servicios:</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Emergent Platform:</strong> Hosting y infraestructura del sitio web</li>
              <li><strong>MongoDB:</strong> Almacenamiento de base de datos</li>
              <li><strong>Google (Analytics, AdSense, OAuth):</strong> Análisis, publicidad, autenticación</li>
              <li><strong>OpenAI (a través de Emergent LLM):</strong> Análisis de nivel de español en ofertas</li>
              <li><strong>Adzuna API:</strong> Agregación de ofertas de empleo</li>
              <li><strong>Swizzonic:</strong> Registro y gestión de dominio</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">Empleadores:</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Cuando hace clic en "Ver Oferta" o "Aplicar", es redirigido al sitio web del empleador. 
              En ese momento, está sujeto a su política de privacidad.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">Requisitos Legales:</h3>
            <p className="text-slate-700 leading-relaxed">
              Podemos divulgar información si lo requiere la ley, orden judicial o autoridades gubernamentales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Transferencias Internacionales de Datos</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sus datos pueden ser transferidos y procesados en países fuera de Suiza/UE, incluyendo:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li><strong>Estados Unidos:</strong> Google (Analytics, AdSense), OpenAI (via Emergent)</li>
              <li><strong>Cloud Services:</strong> Emergent Platform, MongoDB Atlas</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Estos proveedores cumplen con marcos de protección de datos como Privacy Shield, 
              Cláusulas Contractuales Estándar de la UE, o decisiones de adecuación.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Retención de Datos</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Datos de cuenta:</strong> Hasta que elimine su cuenta</li>
              <li><strong>Datos del Newsletter:</strong> Hasta que cancele su suscripción (puede hacerlo en cualquier momento)</li>
              <li><strong>Trabajos guardados y alertas:</strong> Hasta que los elimine o elimine su cuenta</li>
              <li><strong>Logs de servidor:</strong> Máximo 90 días</li>
              <li><strong>Google Analytics:</strong> 26 meses (configuración estándar)</li>
              <li><strong>Datos de cumplimiento legal:</strong> Según lo requiera la ley suiza</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Sus Derechos</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Bajo GDPR y la ley suiza de protección de datos, tiene los siguientes derechos:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Derecho de acceso:</strong> Solicitar una copia de sus datos personales</li>
              <li><strong>Derecho de rectificación:</strong> Corregir datos inexactos o incompletos</li>
              <li><strong>Derecho de supresión:</strong> Solicitar la eliminación de sus datos ("derecho al olvido")</li>
              <li><strong>Derecho de restricción:</strong> Limitar el procesamiento de sus datos</li>
              <li><strong>Derecho de portabilidad:</strong> Recibir sus datos en formato estructurado</li>
              <li><strong>Derecho de oposición:</strong> Oponerse al procesamiento basado en interés legítimo</li>
              <li><strong>Retirar consentimiento:</strong> Retirar consentimiento en cualquier momento (sin afectar legalidad previa)</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Para ejercer sus derechos:</strong> Envíe un email a info@jobsuiza.com con asunto "Solicitud de Privacidad".
              Responderemos dentro de 30 días.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Newsletter</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Ofrecemos un servicio de newsletter gratuito para mantenerle informado sobre las mejores ofertas de trabajo en Suiza.
            </p>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">10.1 Suscripción</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Al suscribirse a nuestro newsletter, recopilamos únicamente su dirección de correo electrónico. 
              La suscripción es voluntaria y se basa en su consentimiento explícito.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">10.2 Contenido del Newsletter</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Nuestro newsletter puede incluir:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li>Ofertas de trabajo destacadas en Suiza para hispanohablantes</li>
              <li>Novedades del Tablón de Anuncios y la comunidad</li>
              <li>Actualizaciones importantes del servicio</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">10.3 Darse de Baja</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Puede cancelar su suscripción al newsletter en cualquier momento:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
              <li>Haciendo clic en el enlace "Darse de baja" incluido en cada email</li>
              <li>Enviando un email a info@jobsuiza.com con asunto "Cancelar Newsletter"</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Su dirección de email será eliminada de nuestra lista de suscriptores inmediatamente tras la cancelación.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Seguridad de Datos</h2>
            <p className="text-slate-700 leading-relaxed mb-4">Implementamos medidas de seguridad técnicas y organizativas:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Encriptación HTTPS/SSL para todas las transmisiones</li>
              <li>Contraseñas encriptadas con bcrypt</li>
              <li>Autenticación JWT y Google OAuth segura</li>
              <li>Acceso restringido a datos personales (solo personal autorizado)</li>
              <li>Monitoreo regular de seguridad</li>
              <li>Copias de seguridad regulares</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Sin embargo, ningún sistema es 100% seguro. Si detecta una brecha de seguridad, contáctenos inmediatamente.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Cookies</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Utilizamos cookies para mejorar su experiencia. Para más información, consulte nuestra{' '}
              <Link to="/cookie-policy" className="text-blue-900 underline hover:text-blue-700">
                Política de Cookies
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Menores de Edad</h2>
            <p className="text-slate-700 leading-relaxed">
              JobSuiza no está dirigido a menores de 16 años. No recopilamos intencionalmente datos de menores. 
              Si es padre/tutor y cree que su hijo nos ha proporcionado datos, contáctenos para eliminarlos.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Cambios a esta Política</h2>
            <p className="text-slate-700 leading-relaxed">
              Podemos actualizar esta política ocasionalmente. Le notificaremos cambios materiales por email o 
              mediante un aviso prominente en el sitio. La fecha de "Última actualización" se actualizará.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Autoridad de Control</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Si no está satisfecho con cómo manejamos sus datos, tiene derecho a presentar una queja ante:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-700 mb-2"><strong>Comisionado Federal de Protección de Datos e Información (FDPIC)</strong></p>
              <p className="text-slate-700 mb-2">Feldeggweg 1, 3003 Berna, Suiza</p>
              <p className="text-slate-700 mb-2">Website: www.edoeb.admin.ch</p>
              <p className="text-slate-700">Email: info@edoeb.admin.ch</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Contacto</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Para cualquier pregunta sobre esta Política de Privacidad o el tratamiento de sus datos:
            </p>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> info@jobsuiza.com</p>
              <p className="text-slate-700">
                <strong>Dirección postal:</strong> Lidia Moyano Acuna, Tägernstrasse 1, 8127 Forch, Suiza
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-blue-900 hover:text-blue-700 underline">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
